<script setup>
import { ref, computed, watch, defineAsyncComponent, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
/***/
import { useApplicationStore } from '@stores/application';
import { useAuthPlanningSubjectsValidationStore } from '@stores/auth/planning/subjects/validation';
import { useHistoryStore } from '@stores/history';
import { useAuthUserStore } from '@stores/auth';
import { useConfigurationStore } from '@stores/configuration';
/***/
import { useAuthUser } from '@compositions/Auth';

// Composants
const Loader = defineAsyncComponent(() => import(/* webpackChunkName: "components" */ '@components/UI/Loader'));
const Layout = defineAsyncComponent(() => import(/* webpackChunkName: "components" */ '@components/Layouts/Authenticated'));
const PlanningGeneratingValidationModal = defineAsyncComponent(() => import(/* webpackChunkName: "components" */ '@components/Partial/Me/Account/AI/Planning/ParametersValidation/ValidationModal'));
const PlanningComputingModal = defineAsyncComponent(() => import(/* webpackChunkName: "components" */ '@components/Partial/Me/Account/AI/Planning/ComputingModal'));

const router = useRouter();
const route = useRoute();
const { t } = useI18n();

// Stores
const applicationStore = useApplicationStore();
const subjectsValidationStore = useAuthPlanningSubjectsValidationStore();
const historyStore = useHistoryStore();
const authUserStore = useAuthUserStore();
const globalConfigurationStore = useConfigurationStore();

const currentApplication = computed(() => applicationStore.getCurrent);

const { authUserHasPermission, authUserIsCustomer } = useAuthUser({});


const allowed = ref(false);

// Retourne le titre de la page
const pageTitle = (() => t('page.me.ai.planning.subjects.validation.title'));

// Vrai si la boîte de dialogue de validation est affichée
const validationModalDisplayed = ref(false);

// Vrai si l'utilisateur vient d'une connexion à un réseau social
const fromSocialNetworkConnection = (route.query['from-social-network-connection'] === '1');

// Validation des sujets à traiter
const subjectsValidation = computed(() => subjectsValidationStore.getValidationByToken(route.params.validationToken));

// Chargement de la validation
const loadingValidation = ref(false);
const loadValidationError = ref(false);
const loadValidation = (() => {
     if(loadingValidation.value) {
         return;
     }

    loadingValidation.value = true;
    loadValidationError.value = false;

    subjectsValidationStore.loadValidationByToken(route.params.validationToken).then(() => {
        validationModalDisplayed.value = subjectsValidation.value;
    }).catch(() => {
        loadValidationError.value = true;
    }).finally(() => {
        loadingValidation.value = false;
    });
});

// Chargement de la configuration globale
const loadingGlobalConfiguration = ref(false);
const loadGlobalConfigurationError = ref(false);
const loadGlobalConfiguration = (() => {
    if(loadingGlobalConfiguration.value) {
        return;
    }

    loadingGlobalConfiguration.value = true;
    loadGlobalConfigurationError.value = false;

    globalConfigurationStore.loadConfiguration().then(() => {
        // Géré par le store
    }).catch(()  => {
        loadGlobalConfigurationError.value = true;
    }).finally(() => {
        loadingGlobalConfiguration.value = false;
    });
});

// Chargement de l'abonnement de l'utilisateur
const loadingUserSubscription = ref(false);
const loadUserSubscriptionError = ref(false);
const loadUserSubscription = (() => {
    if(loadingUserSubscription.value) {
        return;
    }

    loadingUserSubscription.value = true;
    loadUserSubscriptionError.value = false;

    authUserStore.loadCreditSubscription().then(() => {
        // Géré par le store
    }).catch(() => {
        loadUserSubscriptionError.value = true;
    }).finally(() => {
        loadingUserSubscription.value = false;
    });
});

onMounted(() => {
    if(! authUserIsCustomer.value || ! authUserHasPermission('edit task')) {
        router.push({
            name: 'error',
            params: {
                statusCode: 404
            }
        });
    } else {

        loadValidation();
        loadGlobalConfiguration();
        loadUserSubscription();

        // Suppression du paramètre from-subjects-validation en query
        const query = Object.assign({}, route.query);
        setTimeout(() => {
            historyStore.setMustPreserveScrollPosition(true);
            delete query['from-subjects-validation'];
            router.replace({
                query
            });
        }, 1000);

        allowed.value = true;
        document.title = pageTitle() + ' - ' + currentApplication.value.name;
    }
});

// Gestion de la boîte de dialogue d'attente de génération du planning
const planningComputing = computed(() => subjectsValidation.value && subjectsValidation.validatedAt !== null);
const planningComputingModalDisplayed = ref(false);

// On observe la validation des sujets pour afficher la modale de génération du planning
watch(planningComputing, (newValue) => {
    // On affiche la modale sur le changement de la date de validation et si la modale n'est pas déjà affiché
    if(planningComputing.value && ! planningComputingModalDisplayed.value) {
        planningComputingModalDisplayed.value = true;
    }
});

// Fermeture de la modale de génération du planning
const closePlanningComputingModal = (() => {
    planningComputingModalDisplayed.value = false;
    // Redirection vers la page d'accueil
    router.push({
        name: 'home'
    });
});
</script>

<template>

    <loader v-if="loadingValidation || loadingGlobalConfiguration || loadingUserSubscription"></loader>
    <layout v-else-if="allowed">
        <p
            v-if="loadValidationError || loadGlobalConfigurationError || loadUserSubscriptionError"
            class="generic-message generic-message-error"
        >
            {{ $t('page.me.ai.planning.subjects.validation.loadingError') }}
        </p>

        <planning-generating-validation-modal
            v-else-if="validationModalDisplayed && subjectsValidation"
            :subjects-validation="subjectsValidation"
            :default-tab="((fromSocialNetworkConnection) ? 'generic' : 'subjects')"
            v-on:close="validationModalDisplayed = false"
        ></planning-generating-validation-modal>

        <planning-computing-modal
            v-else-if="planningComputingModalDisplayed"
            v-on:close="closePlanningComputingModal()"
        ></planning-computing-modal>
    </layout>
</template>
